import React, { ComponentProps, useContext } from 'react';
import { useSelector } from 'react-redux';
import BaseChannelSidebar from 'src/components/Channels/BaseChannelSidebar';
import { AvatarInfoType } from 'src/components/Channels/ChannelSidebarItem';
import { InboxSidebarItem } from 'src/components/Inbox/InboxSidebarItem';
import { FlagsContext, RouteContext } from 'src/context';
import { InboxPageContext } from 'src/context/inboxPageContext';
import {
  InboxNotificationDetailsResponse,
  NotificationEventType,
} from 'src/entities/Notifications';
import { useNotificationsCounts } from 'src/hooks/useNotificationsCount';
import {
  useDeleteNotificationMutation,
  useGetInboxNotificationsQuery,
  useUpdateInboxNotificationStatusMutation,
} from 'src/services/api/inboxApi';
import { RootState } from 'src/store';
import { NotificationGroups } from 'src/store/notifications/types';
import { ensureUnreachable } from 'src/utils/CommonUtils';
import { getPluralDisplayNameFromCount } from 'src/utils/StringUtils';
import { useChannelNavigationShortcut } from '../Channels/useChannelNavigation';
import { NotificationsActionDropdown } from './NotificationsActionsDropdown';
import { NOTIFICATIONS_STATUSES } from 'src/constants';
import history from 'src/history';

export enum InboxNotificationStatus {
  Read = 'read',
  Sent = 'sent',
}

export interface InboxSidebarItemData {
  id?: string;
  title: string;
  label: string; // TODO(EnableInboxSidebarItemRedesign): legacy prop
  description: string;
  notificationStatus: InboxNotificationStatus;
  avatarInfo: (AvatarInfoType & { email: string; companyName?: string }) | null;
  notificationGroup: NotificationGroups | string;
  channelId: string;
  data: string;
  isRead: boolean;
  timestamp: Date;
  resourceId: string;
  refId: string;
  notificationEventType: NotificationEventType;
}

export const InboxSidebar = () => {
  const {
    EnableFormsNotificationCenter,
    EnableInvoicesNotificationCenter,
    EnableContractsNotificationCenter,
  } = useContext(FlagsContext);
  const { data: inboxNotifications = [] } = useGetInboxNotificationsQuery();
  const { query } = React.useContext(RouteContext);
  const { channelId: activeChannelId } = query as { channelId: string };
  const searchKey = useSelector((state: RootState) => state.ui.searchValue);
  const { setSelectedNotification, selectedNotification } =
    React.useContext(InboxPageContext);
  const [markNotificationAsRead] = useUpdateInboxNotificationStatusMutation();
  const [deleteNotification] = useDeleteNotificationMutation();
  const clients = useSelector((state: RootState) => state.clients.clients);
  const internalUsers = useSelector(
    (state: RootState) => state.users.internalUsers,
  );
  const companyDataMap = useSelector(
    (state: RootState) => state.clients.companyDataMap,
  );
  const allUsers = [...clients, ...internalUsers];
  const { readNotificationCount, unreadNotificationCount } =
    useNotificationsCounts(inboxNotifications || [], allUsers, companyDataMap);

  // on click we want to set secondary sidebar info as notification id
  const onSelectChannel: ComponentProps<
    typeof BaseChannelSidebar
  >['onSelectChannel'] = (sidebarItemOption: InboxSidebarItemData) => {
    // if the selected notification is already selected then do not set it again
    if (selectedNotification?.id === sidebarItemOption.id) return;
    setSelectedNotification({
      ...sidebarItemOption,
      isRead: true,
    });
    if (sidebarItemOption.id) {
      markNotificationAsRead({
        notificationId: sidebarItemOption.id,
        status: NOTIFICATIONS_STATUSES.READ,
      });
    }
  };

  /**
   *
   * @param channel {ChannelOptionData}
   * @description This function is used to delete the notification and is triggered when user presses delete key
   */
  const onDeleteNotificationThroughShortcut = async (
    channel: InboxSidebarItemData,
  ) => {
    if (!channel.id) return;
    await deleteNotification({
      id: channel.id,
    });
    // find index of the deleted channel from all channels
    const deletedChannelIndex = inboxNotifications.findIndex(
      (item) => item.id === channel.id,
    );
    // check that deleted channel is not the last channel in the list
    if (inboxNotifications?.length && deletedChannelIndex !== -1) {
      const nextItem = inboxNotifications[deletedChannelIndex + 1];
      if (nextItem !== undefined) {
        onSelectChannel({ ...nextItem, notificationEventType: nextItem.event });
      } else {
        setSelectedNotification(null);
      }
    }
  };

  React.useEffect(
    () => () => {
      // reset inbox sidebar
      setSelectedNotification(null);
    },
    [],
  );

  /**
   * This function is used to get the notification item view model.
   * @param notification : notification details response item
   * @returns formatted notification item data
   */
  const getNotificationItemsData = (
    notification: InboxNotificationDetailsResponse,
  ) => {
    const { fields } = notification;
    const sender = allUsers.find(
      (c) => c.id === notification.senderId || c.id === fields.senderUserId,
    );
    let senderCompanyName =
      companyDataMap[sender?.fields.companyId || notification.senderId || '']
        ?.name || '';
    const isCompanyUser = !!senderCompanyName;

    if ((!sender && !isCompanyUser) || sender?.entityStatus === 'DELETED') {
      return null; // means we shouldn't show notification
    }

    // when user is deleted show sender name as 'Deleted User'
    const senderName = sender
      ? `${sender.fields.givenName} ${sender.fields.familyName}`
      : isCompanyUser
      ? ''
      : 'Deleted User';

    // the description info holds the notification details header title
    let description = '';

    switch (notification.event) {
      case 'files.new':
        const uploadedFileCount = fields.count || 0;
        description = `${senderName} ${
          senderCompanyName ? `from ${senderCompanyName}` : ''
        } uploaded ${getPluralDisplayNameFromCount('file', uploadedFileCount)}`;
        break;
      case 'forms.completed':
        description = `A form was submitted by ${senderName}`;
        break;
      case 'invoice.paid':
        description = `An invoice was paid by ${
          senderName || senderCompanyName
        }`;
        break;

      case 'esign.completed':
        description = `A contract was signed by ${senderName}`;
        break;

      default:
        return ensureUnreachable(notification.event as never);
    }

    // the avatar info data is used to render the notification sender avatar
    // and the sender name.
    const avatarInfo = {
      email: sender?.fields.email || '',
      isCompanyUser,
      // if the senderName is not empty then the
      // name = senderName & companyName = senderCompanyName
      // there are notifications where a senderName is empty eg. invoice paid by a company
      // in this case:
      // name = senderCompanyName
      // companyName = '' since the sender is the company itself
      name: senderName || senderCompanyName,
      companyName: isCompanyUser && senderName != '' ? senderCompanyName : '',
      imageUrl: isCompanyUser
        ? companyDataMap[
            sender?.fields.companyId || notification.senderId || ''
          ]?.avatarUrl || ''
        : sender?.fields.avatarImageUrl || '',
      fallbackColor: isCompanyUser
        ? companyDataMap[
            sender?.fields.companyId || notification.senderId || ''
          ]?.avatarFallbackColor
        : sender?.fields.fallbackColor,
    };

    const notificationGroup = (fields?.groupingEntityType ||
      fields?.notificationGroup ||
      '') as NotificationGroups;

    return {
      id: notification.id,
      title: notification.title,
      isRead: notification.isRead,
      description,
      avatarInfo,
      notificationGroup,
      channelId: fields.channelId || notification.channelId,
      data: notification.data,
      timestamp: new Date(notification.createdAt),
      resourceId: notification.resourceId,
      refId: notification.ref,
      notificationEventType: notification.event,
    };
  };

  const formattedNotificationsList = React.useMemo(() => {
    if (!inboxNotifications) return [];

    const notifications: InboxSidebarItemData[] = inboxNotifications
      .slice()
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .map(getNotificationItemsData)
      .filter((x) => x) as InboxSidebarItemData[];

    return notifications;
  }, [inboxNotifications, allUsers]);

  const filteredNotificationList = React.useMemo(() => {
    const notificationList = [...formattedNotificationsList];
    // if search value is empty then return all the notifications available in the list
    if (!searchKey) return notificationList;

    const queriedNotifications = notificationList.filter((n) =>
      n.title.toLowerCase().includes(searchKey.toLowerCase()),
    );

    // if the selected notification is not included in the queried result
    // un-select the notification.
    if (selectedNotification) {
      const selectedNotificationExist = queriedNotifications.find(
        (n) => n.id === selectedNotification.id,
      );
      if (!selectedNotificationExist) {
        // if the selected notification is not included in the queried result
        // reset the selected notification state and remove the active channel id from the url.
        // the active notification query param should be in sync with the selected notification state.
        setSelectedNotification(null);
        history.replace({
          search: '',
        });
      }
    }

    // return only those notifications that meet our search criteria
    return queriedNotifications;
  }, [searchKey, formattedNotificationsList, selectedNotification]);

  /**
   * This effect is used to set the selected notification
   * based on the active notification id in the url.
   */
  React.useEffect(() => {
    // when there is already a selected notification
    // then skip this effect.
    if (selectedNotification) return;
    if (activeChannelId) {
      // set selected notification based on active channel id
      const activeNotificationItem = formattedNotificationsList.find(
        (notification) => notification.id === activeChannelId,
      );
      if (activeNotificationItem) {
        setSelectedNotification(activeNotificationItem);
      }
    }
  }, [activeChannelId, formattedNotificationsList, selectedNotification]);

  // handle channel notification shortcuts for inbox list
  useChannelNavigationShortcut(
    filteredNotificationList,
    filteredNotificationList.findIndex(
      (notification) => notification.id === selectedNotification?.id,
    ),
    onSelectChannel,
    onDeleteNotificationThroughShortcut,
  );

  if (formattedNotificationsList.length < 1) return null;

  return (
    <BaseChannelSidebar
      title={
        EnableContractsNotificationCenter ||
        EnableFormsNotificationCenter ||
        EnableInvoicesNotificationCenter
          ? 'Notifications'
          : 'Files Notifications'
      }
      entity="inbox"
      sidebarItemOptions={filteredNotificationList}
      onSelectChannel={onSelectChannel}
      itemRenderer={InboxSidebarItem}
      noCreateAction
      headerActionsSlot={
        <NotificationsActionDropdown
          totalUnreadNotificationsCount={unreadNotificationCount || 0}
          totalReadNotificationsCount={readNotificationCount || 0}
        />
      }
      shouldSetQueryParam={false}
      disableDefaultSelect
    />
  );
};
